// The one vocabulary the coverage surface is drawn in. It lives here rather
// than in each panel because the table, the cards, the tiles and the rollup all
// draw the same two ideas — the boundary between the two role groups, and a
// teacher nobody has visited — and a token written out four times drifts on the
// first edit.
//
// Nothing on this surface may import `utils/helpers/scoreColors`: that palette
// means GRADE, and coverage carries no score. A row coloured from it would read
// as "this teacher is red" when it means "nobody has visited them".
//
// COLOUR IS NEVER THE ONLY SIGNAL. There is exactly one coloured state left
// here — the rose rail below — and the word «ни разу» rides under the zero it
// marks, so the fact survives a greyscale print, a projector and a viewer who
// cannot tell rose from slate.

export const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";

export const CARD = "bg-white rounded-2xl border border-slate-200 shadow-sm";

// The metadata voice used app-wide for column heads and tile labels.
export const META_LABEL =
  "text-[10px] font-bold uppercase tracking-widest text-slate-500";

// Figures are tabular so a column of counts stays aligned as digits change.
export const FIGURE = "font-black tabular-nums tracking-tight";

// The hairline between the two role groups. Written as a BORDER rather than as
// the free-standing `w-px` div the tiles used, because a div cannot live
// between two table cells — and the tiles now draw the same border on a
// zero-width element, so the table's group boundary and the tile's two-figure
// split are literally one device and cannot drift apart.
export const GROUP_EDGE = "border-l border-slate-100";

// The only rail left on this surface, and it means exactly one thing: nobody
// visited this teacher inside the period. It sits on the CELL, never the row —
// a row-level rail would have to summarise two role groups into one colour,
// which is the ambiguity the deleted tab strip used to hide. A zero in «провёл»
// deliberately gets none: eight rose zeros would drown the three that are the
// school's actual coverage gap, and those three are one filter option away.
export const UNCOVERED_RAIL = "bg-rose-400";

// A count a viewer can drill into. Underlined on hover rather than tinted, so
// the digits keep the one weight the eye scans the column by.
//
// The colour is NOT in the base, and that is deliberate: a zero is also
// drillable and has to stay rose or slate, and appending its colour to a token
// that already carries `text-slate-900` resolves by the order Tailwind emits
// the palette, not by the order of the class attribute — it would work or not
// by luck. Callers pick a colour and compose.
const NUMBER_BTN_BASE =
  `rounded-md px-1 -mx-1 font-black tabular-nums transition hover:text-indigo-700 hover:underline decoration-2 underline-offset-2 ${FOCUS_RING}`;

export const NUMBER_BTN = `${NUMBER_BTN_BASE} text-slate-900`;

// The same drill-in on a zero. A zero is exactly where the question "so what IS
// there?" gets asked — the answer is usually a booking nobody has run — so the
// cell keeps its own colour and gains the affordance.
export const ZERO_BTN = (uncovered) =>
  `${NUMBER_BTN_BASE} ${uncovered ? "text-rose-600" : "text-slate-400"}`;

export const PRIMARY_BTN =
  `inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-100 transition hover:bg-indigo-700 disabled:opacity-60 ${FOCUS_RING}`;

export const QUIET_BTN =
  `inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed ${FOCUS_RING}`;

export const DANGER_QUIET_BTN =
  `inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-xs font-bold text-rose-600 transition hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed ${FOCUS_RING}`;

// Rows already on screen while the next page is in flight. They stay readable
// and stop taking clicks — blanking the table to a skeleton on every filter
// change makes the page strobe, and leaving them fully live invites a click on
// a row that is about to be replaced.
export const STALE = "opacity-60 pointer-events-none";
